/* eslint-disable react/no-unused-prop-types */
/**
 * eslint is not seeing we are sending the props from GroupDetailV1 to the render functions.
 */

import React from 'react'
import {
  Button,
  CaptionText,
  Card,
  Col,
  Container,
  Divider,
  Elevation,
  Link,
  Row,
} from '@paypalcorp/pp-react'
import Strip from '../components/Strip'
import Activity from './components/Activity/Activity'
import { Edge, ActivityInformation, GroupItem } from '../../../../../typings'
import { Message } from '@paypalcorp/worldready-react'
import TitleAndStatus from './components/TitleAndStatus'
import { ClosingDate } from '../components/ClosingDate'
import ActionButtons from './components/ActionButtons/Buttons'
import MobileActionButtons from './components/ActionButtons/SmallScreenButtons'
import Share from '../components/Share'
import CollectedAmount from './components/CollectedAmount'

import * as styles from '../otherScreensGroupDetail.css'
import * as mobileStyles from '../smallerScreenGroupDetail.css'
import * as commonStyles from '../index.css'
import Description from './components/Description'
import ActivityHeading from './components/Activity/ActivityHeading'
import { MoreOptionsItem } from './components/ActionButtons/MoreOptionsButton'
import CollectedAmountProgress from './components/CollectedAmountProgress'
import {
  type MoreOptionsParams,
  getDesktopOptions,
  getMobileOptions,
} from '../../../utils/groupDetailOverflowMenu'
import { useLocalizedContent } from '../../../hooks/useLocalizedContent'
import ShareMobile from '../components/ShareMobile'
import { useAppSelector } from '../../../store/hooks'
import * as detailsStyles from './index.css'
import PageErrorAlert from '../../../components/PageErrorAlert'
import { isGroupCreationEnabled } from '../../../utils/features'
import CreateScreen from '../../create'

interface GroupDetailProps {
  isSmallerScreen: boolean
  isMobileShareSheetOpen: boolean
  groupId: string
  shareLink: string
  reportLink: string
  groupItem: GroupItem
  isAdmin: boolean
  isActive: boolean
  isPayPalUser: boolean
  isEditEnabled: boolean
  hasFunds: boolean
  totalCount: number
  activities: Edge<ActivityInformation>[]
  formattedCreatorInfo: any
  handleClickOnEdit: () => void
  handleClickOnClose: () => void
  handleClickOnReport: () => void
  handleContributeAsGuest: () => void
  handleContributeAsUser: () => void
  handleClickOnShare: () => void
  handleClickOnCreateGroup: () => void
  handleCloseMobileShareSheet: () => void
  navigateHome: () => void
  handleClickOnTransfer: () => void
  displayBackButton?: boolean
  onClickMoreOptions: () => void
}

const shouldDisplayActivityHeading = (
  isActive: boolean,
  totalCount: number,
  isPayPalUser: boolean,
) => {
  return isActive || totalCount > 0 || !isPayPalUser
}

const renderMobile = (
  props: GroupDetailProps,
  moreOptionsItems: MoreOptionsItem[],
) => {
  const displayCreateButton =
    isGroupCreationEnabled() &&
    !props.isActive &&
    !props.isAdmin &&
    props.isPayPalUser

  const mobileActionButtons =
    props.isPayPalUser && props.isActive ? (
      <MobileActionButtons
        displayShareButton={props.isActive}
        onShare={props.handleClickOnShare}
        displayContributeButton={props.isActive}
        onContribute={props.handleContributeAsUser}
        displayCreateButton={displayCreateButton}
        onCreatePool={props.handleClickOnCreateGroup}
        moreOptionsItems={moreOptionsItems}
        onClickMoreOptions={props.onClickMoreOptions}
      />
    ) : (
      <ActionButtons
        isGuest={false}
        displayCreateButton={displayCreateButton}
        onCreatePool={props.handleClickOnCreateGroup}
        moreOptionsItems={moreOptionsItems}
        onClickMoreOptions={props.onClickMoreOptions}
      />
    )

  return (
    <>
      <ShareMobile
        isOpen={props.isMobileShareSheetOpen}
        shareLink={props.groupItem.shareLink}
        isAdmin={props.isAdmin}
        isPayPalUser={props.isPayPalUser}
        setIsShareSheetOpen={props.handleCloseMobileShareSheet}
        groupId={props.groupId}
      />
      <div className={commonStyles.containerSmallerDivStyles}>
        <div
          className={styles.overlayDetailContainer}
          data-testid="mobileScreenContainer"
        >
          <>
            <Strip
              backgroundImage={props.groupItem.backgroundImageUrl}
              displayEditButton={props.isEditEnabled}
              onEditButtonClick={props.handleClickOnEdit}
              displayBackButton={false}
            />
            <div className={styles.commonDetailContainer}>
              <Container>
                <Row>
                  <Col>
                    <TitleAndStatus
                      text={props.groupItem.name}
                      isClosed={!props.isActive}
                      headingPropsOverride={{ size: 'sm', type: 'strong' }}
                    />
                  </Col>
                </Row>
                <Row>
                  <Col>
                    <CollectedAmount>
                      {props.groupItem.formattedCurrentAmount}
                    </CollectedAmount>
                  </Col>
                  {props.isAdmin && props.hasFunds && (
                    <Col>
                      <Button
                        variant="secondary"
                        data-testid="transferButton"
                        size="sm"
                        className={detailsStyles.transferButton}
                        onClick={props.handleClickOnTransfer}
                      >
                        <Message id="flatProgress.transfer" />
                      </Button>
                    </Col>
                  )}
                </Row>
                {props.groupItem.targetAmount && (
                  <Row>
                    <Col>
                      <CollectedAmountProgress
                        targetAmount={props.groupItem.targetAmount}
                        collectedAmount={props.groupItem.collectedAmount}
                        formattedTargetAmount={
                          props.groupItem.formattedTargetAmount
                        }
                        formattedCollectedAmount={
                          props.groupItem.formattedCollectedAmount
                        }
                        linearProgressMeterPropsOverride={{ size: 'sm' }}
                      />
                    </Col>
                  </Row>
                )}
                {mobileActionButtons}

                <Divider />

                <Description
                  text={props.groupItem.description}
                  creator={props.formattedCreatorInfo}
                />

                {props.activities.length > 0 && <Divider />}

                {shouldDisplayActivityHeading(
                  props.isActive,
                  props.totalCount,
                  props.isPayPalUser,
                ) && (
                  <Row>
                    <Col>
                      <ActivityHeading />
                    </Col>
                  </Row>
                )}
                <Activity
                  groupId={props.groupId}
                  isPayPalUser={props.isPayPalUser}
                  activities={props.activities}
                  totalCountActivities={props.totalCount ?? 0}
                  groupStatus={props.groupItem.status}
                  onContribute={props.handleContributeAsUser}
                  isAdmin={props.isAdmin}
                />

                {!props.isAdmin && (
                  <Row>
                    <Col>
                      <ReportLinkSection
                        reportLink={props.reportLink}
                        handleClickOnReport={props.handleClickOnReport}
                      />
                    </Col>
                  </Row>
                )}
              </Container>
            </div>
          </>
          {props.isActive && (
            <div
              className={mobileStyles.bottomShareContributeStyles}
              data-testid="mobileBottomElevation"
            >
              <Elevation level={2}>
                <Container className={mobileStyles.elevationDivStyles}>
                  <Row>
                    <Col>
                      <ClosingDate
                        closingDate={props.groupItem.formattedTargetDate}
                      />
                    </Col>
                  </Row>
                  {!props.isPayPalUser && (
                    <ActionButtons
                      sideBySideLayout={false}
                      isGuest={true}
                      displayContributeButton={props.isActive}
                      onGuestContribute={props.handleContributeAsGuest}
                      onClickMoreOptions={props.onClickMoreOptions}
                    />
                  )}
                </Container>
              </Elevation>
            </div>
          )}
        </div>
      </div>
    </>
  )
}

const shouldDisplayReportLink = (
  isPayPalUser: boolean,
  isActive: boolean,
  isAdmin: boolean,
) => {
  const isGuestOnClosedGroup = !isPayPalUser && !isActive
  const isNonCreatorUserOnClosedGroup =
    !isGroupCreationEnabled() && isPayPalUser && !isActive && !isAdmin
  return isGuestOnClosedGroup || isNonCreatorUserOnClosedGroup
}

const renderDesktop = (
  props: GroupDetailProps,
  moreOptionsItems: MoreOptionsItem[],
) => {
  const displayCreateButton =
    isGroupCreationEnabled() &&
    !props.isActive &&
    !props.isAdmin &&
    props.isPayPalUser

  return (
    <div className={commonStyles.containerSmallerDivStyles}>
      <div
        className={styles.overlayDetailContainer}
        data-testid="desktopScreenContainer"
      >
        <>
          <Strip
            backgroundImage={props.groupItem.backgroundImageUrl}
            displayEditButton={props.isEditEnabled}
            onEditButtonClick={props.handleClickOnEdit}
            displayBackButton={props.displayBackButton}
            onBackButtonClick={props.navigateHome}
          />
          <Elevation level={2} className={commonStyles.elevationStyles}>
            <Card className={commonStyles.cardStyles}>
              <div
                className={
                  props.isEditEnabled
                    ? styles.ownerDetailContainer
                    : styles.detailContainerPadding
                }
              >
                <Container>
                  <Row>
                    <Col>
                      <TitleAndStatus
                        text={props.groupItem.name}
                        isClosed={!props.isActive}
                        headingPropsOverride={{ size: 'lg' }}
                      />
                    </Col>
                  </Row>
                  <Row>
                    <Col>
                      <CollectedAmount>
                        {props.groupItem.formattedCurrentAmount}
                      </CollectedAmount>
                    </Col>
                  </Row>
                  {props.groupItem.targetAmount && (
                    <Row>
                      <Col>
                        <CollectedAmountProgress
                          targetAmount={props.groupItem.targetAmount}
                          collectedAmount={props.groupItem.collectedAmount}
                          formattedTargetAmount={
                            props.groupItem.formattedTargetAmount
                          }
                          formattedCollectedAmount={
                            props.groupItem.formattedCollectedAmount
                          }
                          linearProgressMeterPropsOverride={{ size: 'sm' }}
                        />
                      </Col>
                    </Row>
                  )}
                  {props.isActive && (
                    <Row>
                      <Col>
                        <ClosingDate
                          closingDate={props.groupItem.formattedTargetDate}
                        />
                      </Col>
                    </Row>
                  )}
                  {(props.isPayPalUser || props.isActive) && (
                    <ActionButtons
                      isGuest={!props.isPayPalUser}
                      displayTransferButton={props.isAdmin && props.hasFunds}
                      handleClickOnTransfer={props.handleClickOnTransfer}
                      displayContributeButton={props.isActive}
                      onContribute={props.handleContributeAsUser}
                      onGuestContribute={props.handleContributeAsGuest}
                      displayCreateButton={displayCreateButton}
                      onCreatePool={props.handleClickOnCreateGroup}
                      moreOptionsItems={moreOptionsItems}
                      onClickMoreOptions={props.onClickMoreOptions}
                    />
                  )}

                  <Divider />

                  <Description
                    text={props.groupItem.description}
                    creator={props.formattedCreatorInfo}
                  />

                  {props.isActive ||
                    (props.activities.length > 0 && <Divider />)}

                  {props.isActive && (
                    <Row>
                      <Col>
                        <Share
                          shareLink={props.shareLink}
                          groupName={props.groupItem.name}
                          isAdmin={props.isAdmin}
                          isPayPalUser={props.isPayPalUser}
                          groupId={props.groupId}
                        />
                      </Col>
                    </Row>
                  )}

                  {shouldDisplayActivityHeading(
                    props.isActive,
                    props.totalCount,
                    props.isPayPalUser,
                  ) && (
                    <Row>
                      <Col>
                        <ActivityHeading />
                      </Col>
                    </Row>
                  )}
                  <Activity
                    groupId={props.groupId}
                    isAdmin={props.isAdmin}
                    isPayPalUser={props.isPayPalUser}
                    activities={props.activities}
                    totalCountActivities={props.totalCount ?? 0}
                    groupStatus={props.groupItem.status}
                    onContribute={props.handleContributeAsUser}
                  />
                </Container>
              </div>
            </Card>
          </Elevation>
          {shouldDisplayReportLink(
            props.isPayPalUser,
            props.isActive,
            props.isAdmin,
          ) && (
            <Row>
              <Col>
                <ReportLinkSection
                  reportLink={props.reportLink}
                  handleClickOnReport={props.handleClickOnReport}
                />
              </Col>
            </Row>
          )}
        </>
      </div>
    </div>
  )
}

const ReportLinkSection = ({
  reportLink,
  handleClickOnReport,
}: {
  reportLink: string
  handleClickOnReport: () => void
}) => {
  return (
    <div
      className={mobileStyles.reportGroupStyles}
      data-testid="reportLinkSection"
    >
      <CaptionText
        type="strong"
        as="p"
        className={commonStyles.description}
        style={{ fontWeight: 'bolder' }}
      >
        <Link
          href={reportLink}
          target="_blank"
          onClick={handleClickOnReport}
          size="md"
        >
          <Message id="detail.reportGroup" />
        </Link>
      </CaptionText>
    </div>
  )
}

const GroupDetailV1Dot1 = (props: GroupDetailProps) => {
  const { getContent } = useLocalizedContent()
  const isCreateGroupFlow = useAppSelector(
    ({ sheets }) => sheets.createGroupFlow,
  )
  const moreOptionsParams: MoreOptionsParams = {
    isActive: props.isActive,
    isAdmin: props.isAdmin,
    isPayPalUser: props.isPayPalUser,
    reportLink: props.reportLink,
    onCreateClick: props.handleClickOnCreateGroup,
    onCloseClick: props.handleClickOnClose,
    onReportClick: props.handleClickOnReport,
    getContent,
  }

  return (
    <>
      <CreateScreen
        isCreateGroupFlow={isCreateGroupFlow}
        redirectHomeAfterClosing={false}
      />
      <div className={detailsStyles.errorAlertContainer}>
        <PageErrorAlert />
      </div>
      {props.isSmallerScreen
        ? renderMobile(props, getMobileOptions(moreOptionsParams))
        : renderDesktop(props, getDesktopOptions(moreOptionsParams))}
    </>
  )
}

export default GroupDetailV1Dot1
